import React, { useContext, useEffect, useState } from "react";
import axios from "../api/axios";
import { PreferencesContext } from "../context/PreferencesContext";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  Stack,
  IconButton,
} from "@mui/material";
import { Add, Edit, Delete } from "@mui/icons-material";
import CommonTable from "../components/CommonTable";
import { useMessage } from "../context/MessageContext";
import { useConfirm } from "../context/ConfirmContext";
import { ContentCopy } from "@mui/icons-material";
import MappingPriceDialog from "../components/MappingPriceDialog";

const formatCurrency = (value) =>
  value == null
    ? "—"
    : new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR",
        maximumFractionDigits: 0,
      }).format(value);

const SaleItemMapping = () => {
  const { organization, academicYear } = useContext(PreferencesContext);
  const { showMessage } = useMessage();
  const { confirm } = useConfirm();

  const [branches, setBranches] = useState([]);
  const [masters, setMasters] = useState({ grades: [], programs: [] });
  const [catalog, setCatalog] = useState({ items: [], collections: [] });
  const [mappings, setMappings] = useState([]);

  const [open, setOpen] = useState(false);
  const [editMapping, setEditMapping] = useState(null);
  const [form, setForm] = useState({
    branch: "",
    grade: "",
    program: "",
  });
  const [itemsOptions, setItemsOptions] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [itemEntry, setItemEntry] = useState(null);

  const [priceDialogOpen, setPriceDialogOpen] = useState(false);
  const [priceDialogLoading, setPriceDialogLoading] = useState(false);
  const [priceDialogData, setPriceDialogData] = useState(null);
  const [priceDialogLabel, setPriceDialogLabel] = useState("");

  useEffect(() => {
    const fetchAll = async () => {
      const token = localStorage.getItem("token");
      const [branchRes, mastersRes, catRes, mapRes] = await Promise.all([
        axios.get(`/branches?orgId=${organization}`, {
          headers: { "x-auth-token": token },
        }),
        axios.get(`/masters/${organization}`, {
          headers: { "x-auth-token": token },
        }),
        axios.get(
          `/catalogs/all?orgId=${organization}&academicYear=${academicYear}`,
          { headers: { "x-auth-token": token } }
        ),
        axios.get(
          `/sale-item-mapping?organization=${organization}&academicYear=${academicYear}`,
          { headers: { "x-auth-token": token } }
        ),
      ]);
      setBranches(branchRes.data);
      setMasters(mastersRes.data);
      // setCatalog(catRes.data || []);
      setCatalog(Array.isArray(catRes.data) ? catRes.data : []);
      // setCatalog(catRes.data || { items: [], collections: [] });
      setMappings(mapRes.data || []);
    };
    if (organization && academicYear) fetchAll();
  }, [organization, academicYear, open]);

  useEffect(() => {
    const itemOptions = [];
    const collectionOptions = [];

    (Array.isArray(catalog) ? catalog : []).forEach((cat) => {
      (cat.items || []).forEach((it) => {
        // Check if item matches grade + program
        if (
          (!it.grades || it.grades.includes(form.grade)) &&
          (!it.programs || it.programs.includes(form.program))
        ) {
          itemOptions.push({
            label: `${it.name} (${cat.vendor.name || "-"})`,
            id: it._id,
            isCollection: false,
            price: it.price,
            vendorName: cat.vendor.name || "-",
          });
        }
      });

      (cat.collections || []).forEach((col) => {
        if (
          (!col.grades || col.grades.includes(form.grade)) &&
          (!col.programs || col.programs.includes(form.program))
        ) {
          collectionOptions.push({
            label: `[Set] ${col.name} (${cat.vendor.name || "-"})`,
            id: col._id,
            isCollection: true,
            price: col.price,
            vendorName: cat.vendor.name || "-",
          });
        }
      });
    });

    setItemsOptions([...itemOptions, ...collectionOptions]);
  }, [catalog, form.grade, form.program]);

  useEffect(() => {
    if (editMapping) {
      setForm({
        branch: editMapping.branch || "",
        grade: editMapping.grade,
        program: editMapping.program,
      });
      const selected = [
        ...(editMapping.items || []).map((it) => ({
          id: it.item._id,
          label: it.item.name,
          isCollection: false,
          quantity: it.quantity,
          price: it.item?.price,
        })),
        ...(editMapping.collections || []).map((col) => ({
          id: col.collection._id,
          label: "[Set] " + col.collection.name,
          isCollection: true,
          quantity: col.quantity,
          price: col.collection?.price,
        })),
      ];
      setSelectedItems(selected);
    } else {
      setForm({ branch: "", grade: "", program: "" });
      setSelectedItems([]);
    }
  }, [editMapping]);

  const resetDialog = () => {
    setEditMapping(null);
    setForm({ branch: "", grade: "", program: "" });
    setSelectedItems([]);
    setItemEntry(null);
  };

  const handleAddItem = () => {
    if (
      itemEntry &&
      !selectedItems.some(
        (it) =>
          it.id === itemEntry.id && it.isCollection === itemEntry.isCollection
      )
    ) {
      setSelectedItems([...selectedItems, itemEntry]);
      setItemEntry(null);
    }
  };

  const handleSave = async () => {
    if (!organization || !academicYear) {
      showMessage("Select organization and academic year first", "warning");
      return;
    }
    if (!form.grade || !form.program || !form.branch) {
      showMessage("Branch, Grade and Program are required", "warning");
      return;
    }

    if (selectedItems.length === 0) {
      showMessage("Add at least one item or collection", "warning");
      return;
    }

    try {
      await axios.post("/sale-item-mapping", {
        organization,
        academicYear,
        ...form,
        items: selectedItems
          .filter((it) => !it.isCollection)
          .map((it) => ({
            item: it.id,
            quantity: it.quantity,
          })),
        collections: selectedItems
          .filter((it) => it.isCollection)
          .map((it) => ({
            collection: it.id,
            quantity: it.quantity,
          })),
      });
      setOpen(false);
      resetDialog();
      showMessage("Mapping saved", "success");
    } catch (err) {
      showMessage(err.response?.data?.msg || "Failed to save mapping", "error");
    }
  };

  const handleOpenPricing = async (mapping) => {
    const branchName =
      branches.find((b) => b._id === mapping.branch)?.name || "";
    setPriceDialogLabel(
      [branchName, mapping.grade, mapping.program].filter(Boolean).join(" · ")
    );
    setPriceDialogOpen(true);
    setPriceDialogLoading(true);
    setPriceDialogData(null);

    try {
      const res = await axios.get(
        `/sale-item-mapping/${mapping._id}/pricing?organization=${organization}&academicYear=${academicYear}`
      );
      setPriceDialogData(res.data);
    } catch (err) {
      showMessage(
        err.response?.data?.msg || "Failed to load price breakdown",
        "error"
      );
      setPriceDialogOpen(false);
    } finally {
      setPriceDialogLoading(false);
    }
  };

  const dialogTotalPrice = selectedItems.reduce(
    (sum, item) => sum + (item.price || 0) * (item.quantity || 1),
    0
  );

  const handleDelete = async (id) => {
    const confirmed = await confirm({
      title: "Delete Mapping",
      message: "Are you sure you want to delete this mapping?",
    });
    if (!confirmed) return;
    const token = localStorage.getItem("token");
    await axios.delete(`/sale-item-mapping/${id}`, {
      headers: { "x-auth-token": token },
    });
    setMappings((list) => list.filter((m) => m._id !== id));
    showMessage("Mapping deleted", "success");
  };

  const handleDuplicate = (m) => {
    // Pre-fill form except branch
    setForm({
      branch: "", // Leave branch empty
      grade: m.grade,
      program: m.program,
    });

    // Pre-fill selected items/collections
    const selected = [
      ...(m.items || []).map((it) => ({
        id: it.item._id,
        label: it.item.name,
        isCollection: false,
        quantity: it.quantity,
        price: it.item?.price,
      })),
      ...(m.collections || []).map((col) => ({
        id: col.collection._id,
        label: "[Set] " + col.collection.name,
        isCollection: true,
        quantity: col.quantity,
        price: col.collection?.price,
      })),
    ];
    setSelectedItems(selected);

    setEditMapping(null); // Ensure it's treated as a new mapping
    setOpen(true);
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Student Book Mapping
      </Typography>
      <Button
        variant="contained"
        startIcon={<Add />}
        onClick={() => {
          resetDialog();
          setOpen(true);
        }}
        sx={{ mb: 2 }}
      >
        Add Mapping
      </Button>
      <CommonTable
        rows={mappings}
        orderBy={"Branch"}
        columns={[
          {
            label: "Branch",
            field: "branch",
            sortable: true,
            getSortValue: (m) =>
              branches.find((b) => b._id === m.branch)?.name.toLowerCase() ||
              "",
            getSearchValue: (m) =>
              branches.find((b) => b._id === m.branch)?.name.toLowerCase() ||
              "",
            renderCell: (m) =>
              branches.find((b) => b._id === m.branch)?.name || "",
          },
          { label: "Grade", field: "grade" },
          { label: "Program", field: "program" },
          {
            label: "Items",
            sortable: false,
            renderCell: (m) =>
              (m.items || [])
                .map((i) => `${i.item?.name} (${i.quantity})`)
                .join("\n"),
          },
          {
            label: "Collections",
            sortable: false,
            renderCell: (m) =>
              (m.collections || [])
                .map((c) => `${c.collection?.name} (${c.quantity})`)
                .join("\n"),
          },
          {
            label: "Total Price",
            field: "totalPrice",
            sortable: true,
            getSortValue: (m) => m.pricing?.totalPrice ?? 0,
            renderCell: (m) => {
              const total = m.pricing?.totalPrice;
              const hasWarnings = (m.pricing?.warnings || []).length > 0;
              if (total == null && !m.pricing?.lineCount) {
                return "—";
              }
              return (
                <Button
                  variant="text"
                  size="small"
                  color={hasWarnings ? "warning" : "primary"}
                  onClick={() => handleOpenPricing(m)}
                  sx={{ textTransform: "none", fontWeight: 600 }}
                >
                  {formatCurrency(total)}
                  {hasWarnings ? " ⚠" : ""}
                </Button>
              );
            },
          },
          {
            label: "Actions",
            sortable: false,
            renderCell: (m) => (
              <>
                <IconButton
                  onClick={() => {
                    setEditMapping(m);
                    setOpen(true);
                  }}
                >
                  <Edit />
                </IconButton>

                <IconButton onClick={() => handleDelete(m._id)}>
                  <Delete />
                </IconButton>
                <IconButton onClick={() => handleDuplicate(m)}>
                  <ContentCopy />
                </IconButton>
              </>
            ),
          },
        ]}
      />

      <Dialog
        open={open}
        onClose={() => {
          resetDialog();
          setOpen(false);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{editMapping ? "Edit" : "Add"} Mapping</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="normal">
            <InputLabel>Branch</InputLabel>
            <Select
              value={form.branch}
              onChange={(e) =>
                setForm((f) => ({ ...f, branch: e.target.value }))
              }
              label="Branch"
            >
              {branches.map((b) => (
                <MenuItem key={b._id} value={b._id}>
                  {b.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="normal">
            <InputLabel>Grade</InputLabel>
            <Select
              value={form.grade}
              onChange={(e) =>
                setForm((f) => ({ ...f, grade: e.target.value }))
              }
              label="Grade"
            >
              {masters.grades.map((g) => (
                <MenuItem key={g} value={g}>
                  {g}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="normal">
            <InputLabel>Program</InputLabel>
            <Select
              value={form.program}
              onChange={(e) =>
                setForm((f) => ({ ...f, program: e.target.value }))
              }
              label="Program"
            >
              {masters.programs.map((p) => (
                <MenuItem key={p} value={p}>
                  {p}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Typography sx={{ mt: 2, mb: 1 }}>Add Item/Collection (Books)</Typography>
          <Stack direction="row" spacing={1} alignItems="center" mb={2}>
            <Autocomplete
              options={itemsOptions.filter(
                (opt) => !selectedItems.some((sel) => sel.id === opt.id)
              )}
              getOptionLabel={(opt) => opt.label}
              value={itemEntry}
              onChange={(e, v) =>
                setItemEntry(v ? { ...v, quantity: 1 } : null)
              }
              renderInput={(params) => (
                <TextField {...params} label="Select Item/Collection (Books)" />
              )}
              sx={{ flex: 1 }}
            />
            <TextField
              label="Qty"
              type="number"
              value={itemEntry?.quantity || 1}
              onChange={(e) => {
                const q = Math.max(1, parseInt(e.target.value, 10) || 1);
                setItemEntry((entry) =>
                  entry ? { ...entry, quantity: q } : entry
                );
              }}
              sx={{ width: 90 }}
              disabled={!itemEntry}
            />
            <Button
              variant="contained"
              onClick={handleAddItem}
              disabled={!itemEntry}
            >
              Add
            </Button>
          </Stack>

          {selectedItems.length > 0 && (
            <Typography variant="subtitle2" sx={{ mt: 2 }}>
              Preview total:{" "}
              <strong>{formatCurrency(dialogTotalPrice)}</strong>
            </Typography>
          )}

          <Stack spacing={1}>
            {selectedItems.map((item, idx) => (
              <Box key={item.id} display="flex" alignItems="center">
                <Chip
                  label={`${item.label} · ${formatCurrency(item.price)}`}
                  sx={{ mr: 2 }}
                />
                <TextField
                  label="Qty"
                  type="number"
                  value={item.quantity}
                  onChange={(e) => {
                    const q = Math.max(1, parseInt(e.target.value, 10) || 1);
                    setSelectedItems((list) =>
                      list.map((it, i) =>
                        i === idx ? { ...it, quantity: q } : it
                      )
                    );
                  }}
                  sx={{ width: 90, mr: 2 }}
                />
                <Button
                  size="small"
                  color="error"
                  onClick={() =>
                    setSelectedItems((list) => list.filter((_, i) => i !== idx))
                  }
                >
                  Remove
                </Button>
              </Box>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <MappingPriceDialog
        open={priceDialogOpen}
        onClose={() => setPriceDialogOpen(false)}
        loading={priceDialogLoading}
        pricing={priceDialogData}
        mappingLabel={priceDialogLabel}
      />
    </Box>
  );
};

export default SaleItemMapping;
